import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { sanitizeRedirect } from "@/lib/safeRedirect";


type LinkState = "checking" | "ready" | "invalid";

function parseHashError(): { code: string | null; description: string | null } {
  if (typeof window === "undefined") return { code: null, description: null };
  const hash = window.location.hash?.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const params = new URLSearchParams(hash || "");
  const queryParams = new URLSearchParams(window.location.search || "");
  const code = params.get("error_code") || params.get("error") || queryParams.get("error_code") || queryParams.get("error");
  const description =
    params.get("error_description") || queryParams.get("error_description");
  return { code, description: description ? decodeURIComponent(description.replace(/\+/g, " ")) : null };
}

function friendlyLinkError(code: string | null, description: string | null): string {
  const d = (description || "").toLowerCase();
  const c = (code || "").toLowerCase();
  if (c.includes("expired") || d.includes("expired")) {
    return "This reset link has expired. Request a fresh link below — they're valid for 1 hour.";
  }
  if (c.includes("invalid") || d.includes("invalid") || d.includes("not found")) {
    return "This reset link isn't valid. It may have already been used. Request a new one below.";
  }
  if (c.includes("otp") || d.includes("otp")) {
    return "This reset link couldn't be verified. Request a new one below.";
  }
  return description || "We couldn't verify your reset link. Request a new one below.";
}

function friendlyUpdateError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("same") && m.includes("password")) {
    return "New password must be different from your current password.";
  }
  if (m.includes("weak") || m.includes("pwned") || m.includes("compromised")) {
    return "That password has been seen in a data breach. Please choose a different one.";
  }
  if (m.includes("session") || m.includes("expired") || m.includes("jwt")) {
    return "Your reset session expired. Request a new reset link to continue.";
  }
  if (m.includes("network") || m.includes("fetch")) {
    return "Network issue — check your connection and try again.";
  }
  return "Couldn't update your password. Please try again.";
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const safeRedirectRaw = sanitizeRedirect(searchParams.get("redirect"));
  // Hash-only targets land on "/" so they hit the landing-page section.
  const safeRedirect = safeRedirectRaw?.startsWith("#") ? `/${safeRedirectRaw}` : safeRedirectRaw;
  const postResetTarget = safeRedirect || "/dashboard";
  const [state, setState] = useState<LinkState>("checking");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    // First — check if the URL surfaced an error from Supabase (expired/invalid link)
    const { code, description } = parseHashError();
    if (code) {
      setLinkError(friendlyLinkError(code, description));
      setState("invalid");
      return;
    }

    let settled = false;

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (settled) return;
      if (event === "PASSWORD_RECOVERY" || (session && event === "SIGNED_IN")) {
        settled = true;
        setState("ready");
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (settled) return;
      if (data.session) {
        settled = true;
        setState("ready");
      }
    });

    // If neither a recovery event nor an existing session shows up, treat as invalid link
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        setLinkError("This reset link isn't valid or has already been used. Request a new one below.");
        setState("invalid");
      }
    }, 4000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);

    if (password.length < 8) {
      setFieldError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFieldError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      navigate(postResetTarget, { replace: true });
    } catch (err: any) {
      const friendly = friendlyUpdateError(err?.message || "");
      // If session expired mid-flow, escalate to the invalid-link screen
      if (/session|expired|jwt/i.test(err?.message || "")) {
        setLinkError(friendly);
        setState("invalid");
      } else {
        setFieldError(friendly);
        toast.error(friendly);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Set a new password — Trends Discovery</title>
        <meta name="description" content="Choose a new password for your Trends Discovery account. Links expire after one hour for your security." />
        <link rel="canonical" href="https://trendsdiscovery.com/reset-password" />
        <meta property="og:title" content="Set a new password — Trends Discovery" />
        <meta property="og:url" content="https://trendsdiscovery.com/reset-password" />
      </Helmet>
    <div className="min-h-screen grid place-items-center px-4 bg-hero-radial">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-elevated">
        <Link to="/auth" className="font-mono text-xs text-primary">← back</Link>
        <h1 className="font-display text-3xl font-bold mt-4">
          {state === "invalid" ? "Link not valid" : "Set a new password"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {state === "invalid"
            ? "We couldn't verify your reset link."
            : "Choose something strong — at least 8 characters."}
        </p>

        {state === "checking" && (
          <div className="mt-8 text-sm text-muted-foreground font-mono">
            Verifying your reset link…
          </div>
        )}

        {state === "invalid" && (
          <div className="mt-6 space-y-4">
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-foreground">
              <div className="font-mono text-xs text-destructive mb-2">// LINK ISSUE</div>
              {linkError}
            </div>
            <Button asChild variant="hero" size="lg" className="w-full">
              <Link to="/forgot-password">Request a new reset link</Link>
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Or{" "}
              <Link to="/auth" className="text-primary hover:underline">
                return to sign in
              </Link>
            </p>
          </div>
        )}

        {state === "ready" && (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldError) setFieldError(null);
                }}
                autoComplete="new-password"
                aria-invalid={!!fieldError}
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  if (fieldError) setFieldError(null);
                }}
                autoComplete="new-password"
                aria-invalid={!!fieldError}
                disabled={loading}
              />
              {fieldError && (
                <p className="mt-2 text-sm text-destructive">{fieldError}</p>
              )}
            </div>
            <Button type="submit" variant="hero" size="lg" className="w-full" disabled={loading}>
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
    </>
  );
}
